<template>
  <div>
    <DataTable
      v-model:filters="filters"
      v-model:expandedRows="expandedRows"
      :value="items"
      :loading="loading"
      :rows="10"
      lazy
      row-hover
      paginator
      scrollable
      :totalRecords="total"
      :rowsPerPageOptions="[10, 25, 50]"
      dataKey="id"
      filterDisplay="row"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Memuat {first} dari {last} data dari total {totalRecords}"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      :row-class="rowClass"
    >
      <template #empty>
        <div class="text-center">Tidak ada data.</div>
      </template>
      <template #loading>
        <div class="text-center">Memuat data, harap tunggu...</div>
      </template>
      <Column style="min-width: 3rem; flex: initial" header="" expander  />
      <Column
        field="si_no"
        header="No. Faktur"
        style="min-width: 12rem"
        headerStyle="min-width: 12rem"
        sortable
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column
        dataType="date"
        filter-field="order_between"
        field="si_at"
        header="Tgl. Faktur"
        style="min-width: 20rem"
        headerStyle="min-width: 20rem"
        sortable
      >
        <template #body="{ data }">
          {{ formatDate(data.si_at) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <CalendarFilter
            ref="calendarFilter"
            v-model="filterModel.value"
            @select="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="salesman.nama"
        sort-field="salesman"
        filter-field="salesman"
        header="Salesman"
        style="min-width: 12rem"
        headerStyle="min-width: 12rem"
        sortable
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="pelanggan.nama"
        sort-field="pelanggan"
        filter-field="pelanggan"
        header="Pelanggan"
        style="min-width: 12rem"
        headerStyle="min-width: 12rem"
        sortable
      >
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="pelanggan_npwp"
        header="N.P.W.P"
        style="min-width: 12rem"
      />
      <Column
        field="sub_total"
        header="Bruto"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.sub_total, 2, 2) }}
        </template>
      </Column>
      <Column
        field="dpp_si"
        header="Nett Faktur"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.dpp_si, 2, 2) }}
        </template>
      </Column>
      <Column
        field="dpp_si_1112"
        header="DPP"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.dpp_si_1112, 2, 2) }}
        </template>
      </Column>
      <Column
        field="ppn_si"
        header="PPN"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">{{ formatCurrency(data.ppn_si, 2, 2) }} </template>
      </Column>
      <Column
        field="jumlah"
        header="Nilai Penjualan"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.jumlah) }}
        </template>
      </Column>
      <Column
        field="pajak_no"
        header="No Faktur Pajak"
        style="min-width: 12rem"
      />
      <Column
        field="pajak_dpp"
        header="DPP Faktur Pajak"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.pajak_dpp, 2, 2) }}
        </template>
      </Column>
      <Column
        field="pajak_ppn"
        header="PPN Faktur Pajak"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.pajak_ppn, 2, 2) }}
        </template>
      </Column>
      <Column
        field="selisih_dpp"
        header="Selisih DPP"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.selisih_dpp, 2, 2) }}
        </template>
      </Column>
      <Column
        field="selisih_ppn"
        header="Selisih PPN"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.selisih_ppn, 2, 2) }}
        </template>
      </Column>
      <Column
        filter-field="status_lunas"
        header="Tgl Lunas"
        style="min-width: 12rem"
      >
        <template #body="{ data }">
          {{ formatDate(data.date_paid) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <Dropdown
            v-model="filterModel.value"
            :options="list_status"
            optionValue="value"
            optionLabel="label"
            optionDisabled="disabled"
            class="w-full"
            input-class="w-full"
            @change="filterCallback()"
          />
        </template>
      </Column>
      <Column
        field="jumlah"
        header="Jumlah Lunas"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          {{ formatCurrency(data.jumlah) }}
        </template>
      </Column>
      <Column
        field="jumlah_total"
        header="Jumlah Dibayar"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          <span v-if="data.date_paid !== null">{{
            formatCurrency(data.jumlah_total)
          }}</span>
          <span v-else>{{ formatCurrency(0) }}</span>
        </template>
      </Column>
      <Column
        field="cn"
        header="CN"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          <span v-if="data.date_paid !== null">{{
            formatCurrency(data.cn)
          }}</span>
          <span v-else>{{ formatCurrency(0) }}</span>
        </template>
      </Column>
      <Column
        field="dn"
        header="DN"
        style="min-width: 10rem; flex-grow: 1; justify-content: flex-end"
      >
        <template #body="{ data }">
          <span v-if="data.date_paid !== null">{{
            formatCurrency(data.dn)
          }}</span>
          <span v-else>{{ formatCurrency(0) }}</span>
        </template>
      </Column>
      <template #expansion="slotProps">
        <grid-rekonsiliasi-penjualan-retur-detail
          :items="slotProps.data.sales_return"
        />
      </template>
    </DataTable>
  </div>
</template>

<script>
import { formatCurrency, formatDate } from '@/helpers'
import { FilterMatchMode } from 'primevue/api'
import CalendarFilter from '@/components/CalendarFilter'
import GridRekonsiliasiPenjualanReturDetail from '@/components/pajak/GridRekonsiliasiPenjualanReturDetail'

export default {
  components: {
    GridRekonsiliasiPenjualanReturDetail,
    CalendarFilter,
  },
  props: {
    items: {
      type: Array,
      default() {
        return []
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    gridOptions: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      options: this.gridOptions,
      list_status: [],
      expandedRows: [],
      filters: {
        si_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
        order_between: { value: null, matchMode: FilterMatchMode.CONTAINS },
        pelanggan: { value: null, matchMode: FilterMatchMode.CONTAINS },
        salesman: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status_lunas: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
    }
  },
  watch: {
    'options.filters'(value) {
      if (!value.order_between.value) {
        this.$refs.calendarFilter.clearValueOnly()
      }
    },
  },
  mounted() {
    this.list_status = [
      { value: '0', label: 'Belum Lunas' },
      { value: '1', label: 'Sudah Lunas' },
    ]
  },
  methods: {
    onPage(e) {
      e.page = e.page + 1
      e.sortField = this.options.sortField
      e.sortOrder = this.options.sortOrder
      this.options = e
      this.options.filters = this.filters
      this.loadData()
    },
    onSort(e) {
      this.options = e
      this.loadData()
    },
    onFilter() {
      this.options.page = 1
      this.options.filters = this.filters
      this.loadData()
    },
    loadData() {
      this.$emit('request', this.options)
    },
    formatCurrency(value, minDigit = 0, maxDigit = 0) {
      return formatCurrency(value, minDigit, maxDigit)
    },
    formatDate(value) {
      return formatDate(value)
    },
    rowClass(rowData) {
      return rowData.sales_return.length > 0 ? "" : "no-expander";
    }
  },
}
</script>

<style>
.p-datatable .p-datatable-tbody > tr.no-expander > td .p-row-toggler {
  display: none;
}
</style>
